import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import axios from 'axios';
import config from '../../config';
import dayjs from 'dayjs';

import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const CreateVisitorLogForm = ({ unitId, units = [], onVisitorLogCreated, onVisitorLogUpdated, onClose, selectedLog }) => {
    const [formData, setFormData] = useState({
        unit: unitId,
        givenName: '',
        lastName: '',
        mobile: '',
        email: '',
        purpose: '',
        checkIn: new Date().toISOString(),
        checkOut: '',
    });
    const [error, setError] = useState(null);

    // Pre-fill form data if a log is selected
    useEffect(() => {
        if (selectedLog && units.length > 0) {
            setFormData({
                ...selectedLog,
                unit: selectedLog.unit?._id,
                createdBy: selectedLog.createdBy?._id || selectedLog.createdBy,
            });
        }
    }, [selectedLog, units]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSubmit = async () => {
        setError(null);
        try {
            if (selectedLog) {
                // Update existing visitor log
                const response = await axios.put(
                    `${config.API_URL}/v2/visitor/${selectedLog._id}`,
                    formData,
                    {
                        headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`, 'Content-Type': 'application/json'}
                    }
                );

                if (response.status === 200 || response.status === 201) {
                    onVisitorLogUpdated(response.data); // Notify parent component of update
                    toast.success('Visitor log updated successfully.');
                } else {
                    toast.error('Failed to update the visitor log.');
                }
            } else {
                // Create new visitor log
                const response = await axios.post(`${config.API_URL}/v2/visitor`, formData, {
                    headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`,'Content-Type': 'application/json'}
                });

                if (response.status === 201) {
                    onVisitorLogCreated(response.data); // Notify parent component of creation
                    toast.success('Visitor log created successfully.');
                } else {
                    toast.error('Failed to create the visitor log.');
                }
            }

            onClose(); // Close the modal after submission
        } catch (error) {
            setError(`Error ${selectedLog ? 'updating' : 'creating'} the visitor log. Please try again.`);
            console.error(error);
        }
    };

    return (
        <div className="popup-overlay">
            <div className="popup-content">
                <button className="close-popup" onClick={onClose}>
                    <i className="fas fa-times"></i>
                </button>
                <h2 className="text-2xl text-black">{selectedLog ? 'Edit Visitor Log' : 'Add Visitor Log'}</h2>
                <TextField
                    label="Given Name"
                    id="givenName"
                    name="givenName"
                    variant="outlined"
                    fullWidth
                    value={formData.givenName}
                    onChange={handleChange}
                    sx={{ marginBottom: '1rem' }}
                    required
                />
                <TextField
                    label="Last Name"
                    id="lastName"
                    name="lastName"
                    variant="outlined"
                    fullWidth
                    value={formData.lastName}
                    onChange={handleChange}
                    sx={{ marginBottom: '1rem' }}
                    required
                />
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                    <TextField
                        label="Mobile"
                        id="mobile"
                        name="mobile"
                        variant="outlined"
                        fullWidth
                        value={formData.mobile}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            handleChange({ target: { name: "mobile", value } });
                        }}
                        required
                    />
                    <FormControl fullWidth required>
                        <InputLabel id="unit-label">Unit</InputLabel>
                        <Select
                            labelId="unit-label"
                            id="unit"
                            name="unit"
                            value={formData.unit || (units.length === 1 ? units[0].value : '')}
                            label="Unit"
                            onChange={(e) => setFormData((prev) => ({ ...prev, unit: e.target.value }))}
                            disabled={units.length === 1} // 👈 disables only when single-unit mode
                        >
                            {units.map((unit) => (
                                <MenuItem key={unit.value} value={unit.value}>
                                    {unit.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                </div>
                <TextField
                    label="Email"
                    id="email"
                    name="email"
                    variant="outlined"
                    fullWidth
                    value={formData.email}
                    onChange={handleChange}
                    sx={{ marginBottom: '1rem' }}
                    required
                />
                <TextField
                    label="Purpose"
                    id="purpose"
                    name="purpose"
                    variant="outlined"
                    fullWidth
                    value={formData.purpose}
                    onChange={handleChange}
                    sx={{ marginBottom: '1.2rem' }}
                    required
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Check In"
                        value={formData.checkIn && dayjs(formData.checkIn).isValid() 
                            ? dayjs(formData.checkIn) 
                            : null}
                        onChange={(newValue) =>
                            setFormData((prevData) => ({
                                ...prevData,
                                checkIn: newValue && dayjs(newValue).isValid()
                                    ? newValue.toISOString() 
                                    : null,
                            }))
                        }
                        sx={{ width: '100%', marginBottom: '1rem' }}
                        required
                    />
                </LocalizationProvider>
                <button type="button" className="button" onClick={handleSubmit}>{selectedLog ? 'Edit Visitor Log' : 'Add Visitor Log'}</button>
                {error && <div className="error-message">{error}</div>}
            </div>
        </div>
    );
};

export default CreateVisitorLogForm;
